import {
	Paper,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Zoom,
} from '@mui/material';
import { Avatar, Table, Checkbox, Badge } from 'flowbite-react';
import React from 'react';
import { formatCareerPathType, isScoreRangeValid, stringAvatar } from 'shared-utils';
import {
	UserCareerPathType,
	CampusUserWithApplication,
	MeetingStatus,
	TypeOfUsers,
	UserPercentileBucket,
	GetHiringAssessmentListResponse,
} from 'types';
import classNames from 'classnames';
import { useThemeContext } from 'src/context/ThemeProvider';
import { LightTooltip } from 'src/components/recruiterPositions/RecruiterCandidateCardHeader';
import VerifiedTooltip from 'src/components/profile/VerifiedTooltip';
import { HiAcademicCap } from 'react-icons/hi';
import { badgesList } from 'src/components/profile/ProfileTechnicalScorecard';
import { PiInfoFill } from 'react-icons/pi';
import CampusCandidateActionButton from './CampusCandidateActionButton';
import { CandidatesTabsEnum } from 'src/components/recruiterPositions/RecruiterCandidatesContainer2';
import { useParams, useSearchParams } from 'react-router-dom';
import FresherActionButton from '../../freshers/FresherActionButton';
import { PositionCandidatesTabsEnum } from '../../freshersPosition/PositionCandidateListingTabs';
import CandidateCardMobile from './CandidateCardMobile';
import { useQueryClient } from 'react-query';
import DriveCandidatesActionButton from '../../driveCandidates/DriveCandidatesActionButton';
import MatchPercentileCircularBar from 'src/components/driveAdmin/offlineDriveCompaniesList/MatchPercentileCircularBar';

export const getBgColor = (studentData?: UserCareerPathType | 'NA') => {
	if (studentData === UserCareerPathType.SOFTWARE_DEVELOPMENT) {
		return 'bg-sky-100 text-[#1E429F]';
	}
	if (studentData === UserCareerPathType.DATA_ANALYTICS) {
		return 'bg-purple-100 text-[#5521B5]';
	}
	if (studentData === UserCareerPathType.SDET) {
		return 'bg-green-100 text-[#008000]';
	}
	if (studentData === UserCareerPathType.TECH_SUPPORT) {
		return 'bg-teal-100 text-[#005B5B]';
	}
	if (studentData === UserCareerPathType.OPERATIONS) {
		return 'bg-gray-100 text-[#6C757D]';
	}
	if (studentData === UserCareerPathType.BD_EXECUTIVE) {
		return 'bg-orange-100 text-[#FF8C00]';
	}
	return '';
};

export type candidateTableColumnHeaderType = {
	id: string;
	label: string;
	align: 'left' | 'center' | 'right' | 'justify' | 'inherit' | undefined;
	minWidth: string;
	info?: string;
};

interface Props {
	candidates: CampusUserWithApplication[];
	count: number;
	page: number;
	selectedTab: CandidatesTabsEnum | PositionCandidatesTabsEnum;
	setPage: (page: number) => void;
	rowsPerPage: number;
	typeOfUsers: TypeOfUsers;
	showRoundStatus?: boolean;
	showCandidateName?: boolean;
	isPositionCreated?: boolean;
	setRowsPerPage: (rowsPerPage: number) => void;
	totalShortlistedCandidates?: number;
	bulkSelectedCandidates: string[];
	setBulkSelectedCandidates: React.Dispatch<React.SetStateAction<string[]>>;
}

const CandidateList = ({
	candidates,
	count,
	page,
	setPage,
	selectedTab,
	typeOfUsers,
	rowsPerPage,
	setRowsPerPage,
	isPositionCreated,
	bulkSelectedCandidates,
	showRoundStatus = false,
	showCandidateName = true,
	setBulkSelectedCandidates,
	totalShortlistedCandidates,
}: Props) => {
	const { id } = useParams();
	const queryClient = useQueryClient();
	const maxLength = 15;
	const { themeMode } = useThemeContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isSeenByRecruiterFlag, setIsSeenByRecruiterFlag] = React.useState(false);

	const allHiringAssessments = queryClient?.getQueryState(['getAllHiringAssessments']) || { data: [] };

	const hiringAssessmentItem = (allHiringAssessments?.data as GetHiringAssessmentListResponse)?.hiringAssessmentList;

	const filteredAssessment = hiringAssessmentItem?.find(assessment => {
		return assessment?._id === id;
	});
	const qualificationCriteriaBySalary = filteredAssessment?.salaryRange?.min || 0;

	const qualifiedCandidatesAsPerSalaryRange = JSON.parse(searchParams.get('salary') || '[]');

	const tableColumns: candidateTableColumnHeaderType[] = [
		{ id: 'student', label: 'Candidate', align: 'left', minWidth: 'fit-content' },
		{ id: 'profile', label: 'Profile', align: 'center', minWidth: 'fit-content' },
		{ id: 'assessment_type', label: 'Role', align: 'center', minWidth: 'fit-content' },
		{ id: 'assessment_status', label: 'Status', align: 'center', minWidth: 'fit-content' },
		{ id: 'percentile', label: 'Percentile', align: 'center', minWidth: 'fit-content' },
		{ id: 'phone', label: 'Phone', align: 'center', minWidth: 'fit-content' },
		{ id: 'match', label: 'Match %', align: 'center', minWidth: 'fit-content' },
		{
			id: 'skill_level',
			label: 'Skill Level',
			align: 'center',
			minWidth: 'fit-content',
			info: '"Skill Level" indicates the competency level of the candidates in the relevant skills',
		},
		{
			id: 'predicted_salary',
			label: 'Predicted Salary',
			align: 'center',
			minWidth: 'fit-content',
			info: '“Predicted Salary" is an estimate based on candidates’s performance during the assessment.',
		},
		{
			id: 'round_status',
			label: 'Round Status',
			align: 'left',
			minWidth: 'fit-content',
		},
		{
			id: 'action',
			label: 'Action',
			align: 'center',
			minWidth: 'fit-content',
		},
	];
	const bulkSelect = searchParams.get('bulkSelect') === 'true';

	const currYear = new Date().getFullYear();

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
		setBulkSelectedCandidates([]);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		setBulkSelectedCandidates([]);
	};

	const getBadgesWithSkills = (studentData: CampusUserWithApplication) => {
		const badgesWithSkills = badgesList.map(badge => {
			const filteredSkills = (
				studentData?.evaluation?.skillRatings?.length ? studentData?.evaluation?.skillRatings || [] : []
			).filter(item => {
				const score = isScoreRangeValid(Math.round(item.rating));
				return score >= badge.minScore && score < badge.maxScore;
			});
			return { ...badge, skills: filteredSkills };
		});

		// Sort badges by the number of skills, descending
		return badgesWithSkills
			.sort((a, b) => b.minScore - a.minScore)
			?.filter(el => el.skills.length)
			.slice(0, 2);
	};

	const tableRowContent = (tableRow: string, studentData: CampusUserWithApplication) => {
		switch (tableRow.toLowerCase()) {
			case 'student': {
				const name = studentData?.name;
				const email = studentData?.email;
				const avatar = studentData.profile.proctoringImageUrl || '';
				const specialization =
					studentData?.pifMeta?.underGraduate?.specialisation || studentData?.pifMeta?.postGraduate?.specialisation;

				const collegeName =
					studentData?.pifMeta?.underGraduate?.college?.name || studentData?.pifMeta?.postGraduate?.college?.name;

				const isTrusted = studentData?.flags?.isTrusted;

				const minPredictedSalary = studentData?.evaluation?.predictedSalary?.min;
				const maxPredictedSalary = studentData?.evaluation?.predictedSalary?.max;
				const isQualified = filteredAssessment ? maxPredictedSalary >= qualificationCriteriaBySalary : false;

				const isQualifiedForCampusCandidate =
					typeOfUsers !== TypeOfUsers.ASSESSMENT
						? qualifiedCandidatesAsPerSalaryRange?.length
							? parseInt(qualifiedCandidatesAsPerSalaryRange?.[0]?.split('_')[0]) <= maxPredictedSalary &&
							  parseInt(qualifiedCandidatesAsPerSalaryRange?.[0]?.split('_')[1]) >= minPredictedSalary
							: false
						: false;

				return (
					<div className="flex grow-2 gap-2 items-center ">
						<div className="min-w-[80px] relative">
							<Avatar
								rounded
								size="lg"
								className="text-xl"
								img={avatar || ''}
								placeholderInitials={stringAvatar(
									showCandidateName ? name?.trim()?.toUpperCase() || '' : email?.trim()?.toUpperCase(),
								)}
							/>
							{(isQualified || isQualifiedForCampusCandidate) && (
								<div className="absolute bottom-0 left-[50%] translate-x-[-50%] h-2">
									<img src="/assets/reco.svg" alt="verified-tag" />
								</div>
							)}
						</div>

						<div>
							<div className="dark:text-white">
								<div className="flex gap-1">
									{name.length > maxLength ? (
										<div>
											{showCandidateName ? (
												<Tooltip title={name} placement="top">
													<a
														target="_blank"
														href={`${process.env.REACT_APP_APP_ENDPOINT}/profile/${studentData.handler}?company=true`}
														className="cursor-pointer underline text-[18px] font-[600] text-[#1C1C28] flex items-center gap-1 capitalize"
													>
														<span>{name?.toLowerCase()?.substring(0, maxLength)}...</span>
														{isTrusted ? (
															<span>
																<div className="flex flex-col justify-center">
																	<LightTooltip
																		light={!(themeMode === 'dark')}
																		title={<VerifiedTooltip />}
																		placement="bottom"
																		arrow={true}
																		componentsProps={{
																			tooltip: {
																				sx: {
																					border: '0px !important',
																					borderRadius: '4px !important',
																					boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																				},
																			},
																		}}
																	>
																		<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={14} height={14} />
																	</LightTooltip>
																</div>
															</span>
														) : null}
													</a>
												</Tooltip>
											) : null}
											{email ? <p className="text-[#555770] text-xs">{email}</p> : null}
											{specialization || studentData?.profile?.gradYear?.toString().length > 0 ? (
												<p className="text-[#555770] text-xs">
													<div className="flex gap-1 items-center">
														<div>
															<HiAcademicCap className="text-[#00B7C4] dark:text-[#A9EFF2] w-[12px] md:w-[20px] h-[12px] md:h-[20px]" />
														</div>

														<div className="text-[#28293D] dark:text-[#E4E4EB] text-xs md:text-[14px] max-w-[20ch] truncate">
															{specialization ? (
																<>
																	<LightTooltip
																		light={!(themeMode === 'dark')}
																		title={
																			<div className="p-2 text-sm text-center text-[#555770] font-[500]">
																				{specialization}
																			</div>
																		}
																		placement="bottom"
																		arrow={true}
																		componentsProps={{
																			tooltip: {
																				sx: {
																					border: '0px !important',
																					borderRadius: '4px !important',
																					boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																				},
																			},
																		}}
																	>
																		<span className="max-w-[20ch] truncate">{specialization} </span>
																	</LightTooltip>
																	(
																	{studentData?.profile?.gradYear === -1
																		? '2015'
																		: studentData?.profile?.gradYear === 1
																		  ? '2027'
																		  : studentData?.profile?.gradYear <= currYear
																		    ? studentData?.profile?.gradYear
																		    : studentData?.profile?.gradYear || 'future'}
																	)
																</>
															) : studentData?.profile?.gradYear === -1 ? (
																'Graduated before 2015'
															) : studentData?.profile?.gradYear === 1 ? (
																'Graduating after 2027'
															) : studentData?.profile?.gradYear <= currYear ? (
																`Graduated in ${studentData?.profile?.gradYear}`
															) : (
																`Graduating in ${studentData?.profile?.gradYear || 'future'}`
															)}
														</div>
													</div>
												</p>
											) : null}
											{collegeName ? (
												<LightTooltip
													light={!(themeMode === 'dark')}
													title={<div className="p-2 text-sm text-center text-[#555770] font-[500]">{collegeName}</div>}
													placement="bottom"
													arrow={true}
													componentsProps={{
														tooltip: {
															sx: {
																border: '0px !important',
																borderRadius: '4px !important',
																boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
															},
														},
													}}
												>
													<p className="text-[#555770] text-xs ml-[10%] truncate max-w-[10ch]">{collegeName}</p>
												</LightTooltip>
											) : null}
										</div>
									) : (
										<div>
											{showCandidateName ? (
												<a
													target="_blank"
													href={`${process.env.REACT_APP_APP_ENDPOINT}/profile/${studentData.handler}?company=true`}
													className="underline cursor-pointer text-[18px] font-[600] text-[#1C1C28] flex items-center gap-1 capitalize"
												>
													<span>{name?.toLowerCase()}</span>
													{isTrusted ? (
														<span>
															<div className="flex flex-col justify-center">
																<LightTooltip
																	light={!(themeMode === 'dark')}
																	title={<VerifiedTooltip />}
																	placement="bottom"
																	arrow={true}
																	componentsProps={{
																		tooltip: {
																			sx: {
																				border: '0px !important',
																				borderRadius: '4px !important',
																				boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																			},
																		},
																	}}
																>
																	<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={14} height={14} />
																</LightTooltip>
															</div>
														</span>
													) : null}
												</a>
											) : null}
											{email ? <p className="text-[#555770] text-xs">{email}</p> : null}
											{specialization || studentData?.profile?.gradYear?.toString().length > 0 ? (
												<p className="text-[#555770] text-xs">
													<div className="flex gap-1 items-center">
														<div>
															<HiAcademicCap className="text-[#00B7C4] dark:text-[#A9EFF2] w-[12px] md:w-[20px] h-[12px] md:h-[20px]" />
														</div>

														<div className="text-[#28293D] dark:text-[#E4E4EB] text-xs md:text-[14px] max-w-[20ch] truncate">
															{specialization ? (
																<>
																	<LightTooltip
																		light={!(themeMode === 'dark')}
																		title={
																			<div className="p-2 text-sm text-center text-[#555770] font-[500]">
																				{specialization}
																			</div>
																		}
																		placement="bottom"
																		arrow={true}
																		componentsProps={{
																			tooltip: {
																				sx: {
																					border: '0px !important',
																					borderRadius: '4px !important',
																					boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																				},
																			},
																		}}
																	>
																		<span className="max-w-[20ch] truncate">{specialization} </span>
																	</LightTooltip>
																	(
																	{studentData?.profile?.gradYear === -1
																		? '2015'
																		: studentData?.profile?.gradYear === 1
																		  ? '2027'
																		  : studentData?.profile?.gradYear <= currYear
																		    ? studentData?.profile?.gradYear
																		    : studentData?.profile?.gradYear || 'future'}
																	)
																</>
															) : studentData?.profile?.gradYear === -1 ? (
																'Graduated before 2015'
															) : studentData?.profile?.gradYear === 1 ? (
																'Graduating after 2027'
															) : studentData?.profile?.gradYear <= currYear ? (
																`Graduated in ${studentData?.profile?.gradYear}`
															) : (
																`Graduating in ${studentData?.profile?.gradYear || 'future'}`
															)}
														</div>
													</div>
												</p>
											) : null}
											{collegeName ? (
												<LightTooltip
													light={!(themeMode === 'dark')}
													title={<div className="p-2 text-sm text-center text-[#555770] font-[500]">{collegeName}</div>}
													placement="bottom"
													arrow={true}
													componentsProps={{
														tooltip: {
															sx: {
																border: '0px !important',
																borderRadius: '4px !important',
																boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
															},
														},
													}}
												>
													<p className="text-[#555770] text-xs ml-[10%] truncate max-w-[20ch]">{collegeName}</p>
												</LightTooltip>
											) : null}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				);
			}
			case 'profile': {
				return (
					<div>
						<a
							target="_blank"
							href={`${process.env.REACT_APP_APP_ENDPOINT}/profile/${studentData.handler}?company=true`}
							className="flex justify-center items-center cursor-pointer hover:opacity-75 "
						>
							<img src="/assets/Link.svg" alt="Link" />
						</a>
					</div>
				);
			}
			case 'assessment_type': {
				const assessmentType = studentData?.userPreference?.careerPath || 'NA';

				return (
					<div className="flex items-start leading-5 text-center">
						<div
							className={classNames(
								'self-stretch rounded-lg m-auto py-1 px-[20px] text-base font-medium break-words',
								getBgColor(assessmentType),
							)}
							dangerouslySetInnerHTML={{
								__html: formatCareerPathType(assessmentType as UserCareerPathType)?.replaceAll(' ', '<br/>'),
							}}
						></div>
					</div>
				);
			}
			case 'assessment_status': {
				const badeges = getBadgesWithSkills(studentData);
				return (
					<Badge
						className="px-[14.4px] text-center w-fit inline-block rounded-[7.2px] font-[500] text-sm"
						size="sm"
						color={studentData?.assessmentStatus === MeetingStatus.COMPLETED ? 'success' : 'indigo'}
					>
						{studentData?.assessmentStatus === MeetingStatus.COMPLETED
							? 'Completed'
							: studentData?.assessmentStatus === MeetingStatus.PARTIALLYCOMPLETED
							  ? 'Partially Completed'
							  : 'Invited'}
					</Badge>
				);
			}
			case 'percentile': {
				const percentile = studentData?.userPercentileBucket || 'NA';
				return (
					<div className="w-fit py-1 px-2 mx-auto text-[#00B7C4] rounded-full border border-[#00B7C4] bg-[#E5FFFF] flex text-center justify-center items-center">
						{percentile === UserPercentileBucket.TOP_ONE
							? 'Top 1%'
							: percentile === UserPercentileBucket.TOP_TEN
							  ? 'Top 10%'
							  : percentile === UserPercentileBucket.TOP_TWENTY_FIVE
							    ? 'Top 25%'
							    : percentile === UserPercentileBucket.TOP_FIFTY
							      ? 'Top 50%'
							      : 'NA'}
					</div>
				);
			}
			case 'phone': {
				const phone = studentData?.phone;
				return <div>{phone || 'NA'}</div>;
			}
			case 'skill_level': {
				const badeges = getBadgesWithSkills(studentData);

				if (!badeges?.length) return <div>NA</div>;

				return (
					<div className="flex justify-center gap-[18px] text-center mx-auto">
						{badeges?.map((badege, index) => (
							<div key={index} className="flex flex-col">
								<div
									className="flex items-center gap-1 rounded-[6px] px-3 py-[2px]"
									style={{ background: badege?.bg, color: badege?.text }}
								>
									<img src={badege?.icon} alt="" className="max-w-[20px] max-h-[20px]" />
									{badege?.title}
								</div>
								{badege?.skills?.length ? (
									<Tooltip
										title={
											<div className="text-center">{badege?.skills?.map(skill => skill?.skill?.name)?.join(', ')}</div>
										}
										arrow
										placement="bottom"
									>
										<div className="flex justify-center border-b border-[#555770] mx-auto border-dashed w-fit text-center mt-1 text-sm font-[500] text-[#555770]">
											{badege?.skills?.length ? `${badege?.skills?.length} skills` : ''}
										</div>
									</Tooltip>
								) : null}
							</div>
						))}
					</div>
				);
			}
			case 'match': {
				const withoutpercentage = studentData?.evaluation?.level || 0;
				return (
					<div className="w-[150px] m-auto">
						<MatchPercentileCircularBar value={withoutpercentage} />
					</div>
				);
			}
			case 'predicted_salary': {
				const minPredictedSalary = studentData?.evaluation?.predictedSalary?.min;
				const maxPredictedSalary = studentData?.evaluation?.predictedSalary?.max;
				const salaryRangeToShow =
					typeof minPredictedSalary === 'number' && typeof maxPredictedSalary === 'number'
						? `₹${minPredictedSalary} - ${maxPredictedSalary}LPA`
						: 'NA';
				return salaryRangeToShow === 'NA' ? (
					'NA'
				) : (
					<div className="flex flex-col w-fit items-center mx-auto">
						<p className="font-[400] font-inter text-[#8F90A6] text-xs">Salary Range</p>
						<p>{salaryRangeToShow}</p>
					</div>
				);
			}
			case 'round_status': {
				return (
					<FresherActionButton
						showActionButton={false}
						showRoundProgress={true}
						selectedTab={selectedTab}
						studentData={studentData}
						isPositionCreated={isPositionCreated}
					/>
				);
			}
			case 'action': {
				if (typeOfUsers === TypeOfUsers.DRIVE) {
					return <DriveCandidatesActionButton selectedTab={selectedTab} studentData={studentData} />;
				}
				if (typeOfUsers === TypeOfUsers.FRESHER) {
					return (
						<FresherActionButton
							selectedTab={selectedTab}
							studentData={studentData}
							isPositionCreated={isPositionCreated}
						/>
					);
				}

				return (
					<div className="inline-block">
						<CampusCandidateActionButton
							disable={
								bulkSelect ||
								(typeOfUsers === TypeOfUsers.ASSESSMENT && studentData?.assessmentStatus === MeetingStatus.PENDING)
							}
							studentData={studentData}
							showCandidateName={showCandidateName}
							totalShortlistedCandidates={totalShortlistedCandidates}
							showHriingAssessmentUsersOnly={typeOfUsers === TypeOfUsers.ASSESSMENT}
							showShortlistingButton={selectedTab === CandidatesTabsEnum.RECOMMENDATIONS}
							ctaText={typeOfUsers === TypeOfUsers.ASSESSMENT ? 'Schedule Interview' : 'Invite for Drive'}
						/>
					</div>
				);
			}
		}
	};

	const hasAssessmentStatus = candidates.find(candidate => candidate?.assessmentStatus);

	const filteredColumns = tableColumns.filter(column => {
		if (process.env.REACT_APP_NODE_ENV === 'production' && column.id === 'predicted_salary') {
			return false;
		}

		// Filter out `assessment_type` if `hasAssessmentStatus` is true and `assessment_status` if false
		if (
			(hasAssessmentStatus && column.id === 'assessment_type') ||
			(typeOfUsers === TypeOfUsers.FRESHER &&
				column.id === 'assessment_type' &&
				selectedTab === PositionCandidatesTabsEnum.CONNECTED)
		) {
			return false;
		}

		if (
			column.id === 'skill_level' &&
			typeOfUsers === TypeOfUsers.FRESHER &&
			selectedTab === PositionCandidatesTabsEnum.CONNECTED
		) {
			return false;
		}

		if (
			(column.id === 'phone' && typeOfUsers !== TypeOfUsers.FRESHER) ||
			(column.id === 'phone' &&
				typeOfUsers === TypeOfUsers.FRESHER &&
				selectedTab !== PositionCandidatesTabsEnum.CONNECTED)
		) {
			return false;
		}

		if (!hasAssessmentStatus && column.id === 'assessment_status') {
			return false;
		}

		if (
			(typeOfUsers !== TypeOfUsers.FRESHER &&
				column.id === 'round_status' &&
				selectedTab === PositionCandidatesTabsEnum.CONNECTED) ||
			(selectedTab !== PositionCandidatesTabsEnum.CONNECTED && column.id === 'round_status')
		) {
			return false;
		}

		if (typeOfUsers === TypeOfUsers.ASSESSMENT && column.id === 'percentile') {
			return false;
		}
		if (typeOfUsers !== TypeOfUsers.ASSESSMENT && column.id === 'match') {
			return false;
		}
		return true;
	});
	const isSalaryFilterApplied = JSON.parse(searchParams.get('salary') || '[]');

	return (
		<>
			{typeOfUsers === TypeOfUsers.ASSESSMENT || isSalaryFilterApplied?.length ? (
				<div className="flex gap-2 mb-1 lg:mb-2">
					<img src="/assets/reco.svg" alt="verified-tag" width="16px" height="16px" />
					<p className="text-[#555770] font-inter text-xs font-medium leading-4">Recommended Candidates</p>
				</div>
			) : null}

			<div className="flex max-w-[1440px] mx-auto">
				<div className="hidden md:block w-full">
					<Paper
						sx={{
							width: '100%',
							overflow: 'hidden',
							borderRadius: '8px',
							isolation: 'isolate',
							boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
							'&>MuiTableContainer-root': {
								scrollbarWidth: 'auto !important',
							},
						}}
					>
						<TableContainer
							sx={{
								minHeight: !candidates?.length ? 200 : 'auto',
								// WebKit browsers (Chrome, Safari)
								'::-webkit-scrollbar': {
									width: '100px !important',
									display: 'block !important',
									height: '2px !important',
								},
								'::-webkit-scrollbar-thumb': {
									backgroundColor: '#555770 !important',
									borderRadius: '10px !important',
								},
								'::-webkit-scrollbar-track': {
									backgroundColor: '#f5f5f5 !important',
								},

								// Non-WebKit (like Firefox)
								scrollbarWidth: 'auto !important',
							}}
						>
							<Table aria-label="sticky table">
								<TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'pre' }}>
									<TableRow
										sx={{
											'&': {
												background: '#F2F2F5',
												'[data-mode="dark"] &': {
													background: '#18222D',
												},
											},
										}}
									>
										<Zoom in={bulkSelect} mountOnEnter unmountOnExit>
											<TableCell align="center" padding="checkbox">
												<Checkbox
													color="purplePlaced"
													checked={bulkSelectedCandidates.length === candidates.length}
													onChange={() => {
														if (bulkSelectedCandidates.length === candidates.length) {
															setBulkSelectedCandidates([]);
															return;
														}
														setBulkSelectedCandidates(candidates?.map(e => e?._id?.toString()));
													}}
												/>
											</TableCell>
										</Zoom>
										{filteredColumns.map((column, id) =>
											column?.info ? (
												<TableCell
													key={id}
													className="uppercase text-[#1F3343] font-inter font-[500] text-sm"
													align={column.align}
													style={{ minWidth: column.minWidth }}
												>
													<div>
														<div className="flex items-center justify-center gap-1">
															<div>{column.label}</div>
															<LightTooltip
																light={!(themeMode === 'dark')}
																title={
																	<div className="p-2 text-sm text-center text-[#555770] font-[500]">
																		{column?.info}
																	</div>
																}
																placement="bottom"
																arrow={true}
																componentsProps={{
																	tooltip: {
																		sx: {
																			border: '0px !important',
																			borderRadius: '4px !important',
																			boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																		},
																	},
																}}
															>
																<span>
																	<PiInfoFill />
																</span>
															</LightTooltip>
														</div>
													</div>
												</TableCell>
											) : column.id === 'action' ? (
												// Condition for the "Action" column (sticky behavior)
												<TableCell
													key={id}
													className="uppercase text-[#1F3343] font-inter font-[500] text-sm"
													align={column.align}
													style={{
														minWidth: column.minWidth,
														position: 'sticky',
														right: 0,
														zIndex: 2,
														background: '#F2F2F5',
													}}
												>
													{column.label}
												</TableCell>
											) : (
												<TableCell
													className="uppercase text-[#1F3343] font-inter font-[500] text-sm"
													key={id}
													align={column.align}
													style={{ minWidth: column.minWidth }}
												>
													{column.label}
												</TableCell>
											),
										)}
									</TableRow>
								</TableHead>
								<TableBody
									sx={{
										background: '#FFF',
										'&': {
											'[data-mode="dark"] &': {
												background: '#18222D',
											},
										},
									}}
								>
									{candidates?.map((student, id) => {
										const isSelected = bulkSelectedCandidates.find(
											bulkCandidate => bulkCandidate?.toString() === student?._id?.toString(),
										)
											? true
											: false;

										return (
											<TableRow
												aria-checked={isSelected}
												hover
												role="checkbox"
												tabIndex={-1}
												key={id}
												sx={{
													backgroundColor: 'white',
													'&:hover': {
														backgroundColor: '#f5f5f5',
														cursor: 'pointer',
														'.MuiTableCell-root': {
															background: 'transparent',
														},
														'.MuiTableCell-root:last-of-type': {
															background: '#f5f5f5',
														},
													},
												}}
											>
												<Zoom in={bulkSelect} mountOnEnter unmountOnExit>
													<TableCell align="center" padding="checkbox">
														<Checkbox
															color="purplePlaced"
															checked={isSelected}
															onChange={() => {
																if (isSelected) {
																	setBulkSelectedCandidates(prev =>
																		prev.filter(
																			bulkCandidate => bulkCandidate?.toString() !== student?._id?.toString(),
																		),
																	);
																	return;
																}
																setBulkSelectedCandidates(prev => [...prev, student?._id?.toString()]);
															}}
														/>
													</TableCell>
												</Zoom>
												{filteredColumns.map((column, id) => (
													<TableCell
														key={student._id + id}
														align={column.align}
														sx={{
															whiteSpace: 'pre',
															position: column.id === 'action' ? 'sticky' : 'static',
															right: column.id === 'action' ? 0 : 'auto',
															zIndex: column.id === 'action' ? 1 : 'auto',
															backgroundColor: 'white',
															'&:hover': {
																cursor: 'pointer',
															},
														}}
													>
														{tableRowContent(column.id, student)}
													</TableCell>
												))}
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							sx={{
								'&': {
									background: '#FAFAFC',
									'[data-mode="dark"] &': {
										background: '#18222D',
									},
								},
							}}
							rowsPerPageOptions={[5, 10, 25, 100]}
							component="div"
							count={count}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Paper>
				</div>

				<div
					className={classNames('w-full md:hidden', bulkSelect && bulkSelectedCandidates?.length ? 'pb-[40vh]' : '')}
				>
					<div className="space-y-4">
						{candidates?.map((studentData, id) => {
							return (
								<CandidateCardMobile
									key={id}
									bulkSelect={bulkSelect}
									typeOfUsers={typeOfUsers}
									studentData={studentData}
									selectedTab={selectedTab}
									tableColumns={tableColumns}
									isPositionCreated={isPositionCreated}
									showCandidateName={showCandidateName}
									bulkSelectedCandidates={bulkSelectedCandidates}
									setBulkSelectedCandidates={setBulkSelectedCandidates}
									totalShortlistedCandidates={totalShortlistedCandidates}
								/>
							);
						})}
					</div>

					<div className="flex justify-center items-center w-full mt-4">
						<TablePagination
							sx={{
								'&': {
									background: '#FAFAFC',
									'[data-mode="dark"] &': {
										background: '#18222D',
									},
								},
							}}
							rowsPerPageOptions={[5, 10, 25, 100]}
							component="div"
							count={count}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default CandidateList;
